// API Service Layer
// Handles all backend API calls with automatic routing for SIP vs Main app

import axios from 'axios';
import apiConfig, { API_ENDPOINTS } from './apiConfig';
import comingSoonImg from '../images/coming-soon.png';

// Shown wherever a swag item has no image, or its image file can't be found.
// Use as an <img onError> fallback too: onError={(e)=>{ e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }}
export const SWAG_IMAGE_PLACEHOLDER = comingSoonImg;

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: apiConfig.baseURL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add app source header to help backend identify which app is calling
        config.headers['X-App-Source'] = apiConfig.isSIP ? 'SIP' : 'Main';
        
        if (apiConfig.debug) {
            console.log('API Request:', {
                url: config.url,
                method: config.method,
                isSIP: apiConfig.isSIP,
            });
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('auth_token');
            window.location.href = '/';
        }
        
        if (apiConfig.debug) {
            console.error('API Error:', {
                url: error.config?.url,
                status: error.response?.status,
                message: error.message,
            });
        }
        
        return Promise.reject(error);
    }
);

// Helper function to build URL with prefix
const buildUrl = (path) => {
    return `${apiConfig.apiPrefix}${path}`;
};

// Employee API calls
export const employeeAPI = {
    login: (employeeID, password) => {
        return apiClient.post(buildUrl('/Employee/employeeLogin'), {
            EmployeeID: employeeID,
            Password: password,
        });
    },
    
    getEmployee: (employeeID) => {
        return apiClient.get(buildUrl(`/employee/${employeeID}`));
    },
    
    getSelectedOrderItems: (employeeID, programYear) => {
        return apiClient.get(buildUrl(`/employee/${employeeID}/selectedOrderItems/${programYear}`));
    },
    
    getAvailableOrderItems: (employeeID, programYear) => {
        return apiClient.get(buildUrl(`/employee/${employeeID}/availableOrderItems/${programYear}`));
    },

    // SIP: 12-month point disbursement history (reuses the /manager history endpoint)
    getPointsTimeline: (employeeID) => {
        return apiClient.get(buildUrl(`/employee/${employeeID}/manager`));
    },

    // SIP: mark the supervisor's whole order complete (sets OrderComplete = 1).
    completeSIPOrder: (sipSupervisorID) =>
        apiClient.post(buildUrl(`/employee/completeOrder?sipSupervisorId=${sipSupervisorID}`)),

    updateSelections: (employeeID, programYear, selections) => {
        // SIP uses a different DTO shape (SIPUpdateSelectionDto on the server)
        // The selections object is passed through as-is; the server expects:
        //   Main: { EmployeeID, SwagItemID, SizeID, ItemQuantity, PointsUsed }
        //   SIP:  { SIPSupervisorID, SIPSwagItemInventoryID, SwagItemID, SizeID, ItemQuantity, PointsUsed }
        return apiClient.post(buildUrl('/employee/updateSelections'), selections);
    },
    
    generateFormLetter: (employeeId) => {
        return fetch(`${apiConfig.baseURL}${buildUrl(`/Employee/${employeeId}/generateFormLetter`)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'X-App-Source': apiConfig.isSIP ? 'SIP' : 'Main',
            }
        });
    },
};

// Manager API calls
export const managerAPI = {
    getManagerData: (managersEmployeeID) => {
        return apiClient.get(buildUrl(`/Employee/${managersEmployeeID}/manager`));
    },
    
    getManagerEmployees: (programYear, managersEmployeeID) => {
        return apiClient.get(buildUrl(`/Manager/employees/${programYear}/${managersEmployeeID}`));
    },
    
    generateFormLettersForEmployees: (employeeIds) => {
        const params = new URLSearchParams();
        employeeIds.forEach(id => params.append('employeeIds', id));
        
        return apiClient.get(buildUrl(`/Employee/GenerateFormLettersForEmployees?${params.toString()}`), {
            responseType: 'blob',
        });
    },
};

// ==== SIP Admin (separate session, separate token) ====
// Uses its own axios client with the admin_auth_token so admin and supervisor
// sessions don't collide. Endpoints live under {apiPrefix}/admin/*; in the
// SIP build apiPrefix=/sip, so calls go to /sip/admin/login etc.

const adminClient = axios.create({
    baseURL: apiConfig.baseURL,
    headers: { 'Content-Type': 'application/json' }
});

// Logged-in admin's EmployeeID — sent to the scoped GET procs so non-global
// admins only see their management subtree (the proc decides global vs scoped).
const adminEmployeeId = () => localStorage.getItem('admin_employee_id') || '';

adminClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('admin_auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-App-Source'] = 'SIPAdmin';
    return config;
});

adminClient.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('admin_auth_token');
            window.location.hash = '#/admin/login';
        }
        return Promise.reject(error);
    }
);

export const adminAPI = {
    login: (employeeID, password) =>
        adminClient.post(buildUrl('/admin/login'), { EmployeeID: employeeID, Password: password }),
    getPending: () =>
        adminClient.get(buildUrl('/admin/disbursement/pending')),
    getDisbursementByMonth: (year, month) =>
        adminClient.get(buildUrl(`/admin/disbursement/month?year=${year}&month=${month}`)),
    createPeriod: (disbursementDate) =>
        adminClient.post(buildUrl('/admin/disbursement/create'), { DisbursementDate: disbursementDate }),
    ensurePeriod: (year, month) =>
        adminClient.post(buildUrl(`/admin/disbursement/ensure?year=${year}&month=${month}`)),
    updatePeriodDate: (disbursementDate) =>
        adminClient.put(buildUrl('/admin/disbursement/date'), { DisbursementDate: disbursementDate }),
    // Answer the "recordable during period?" question. Pass either periodId
    // (Monthly Points) or year+month (Qualification Maintenance).
    setReportable: ({ periodId, siteGroupKey, reportable, year, month }) =>
        adminClient.put(buildUrl('/admin/disbursement/reportable'), {
            SIPDisbursementDateID: periodId || 0, SiteGroupKey: siteGroupKey,
            Reportable: reportable, Year: year, Month: month
        }),
    updatePoints: (sipPointDisbursementID, newPoints) =>
        adminClient.put(buildUrl('/admin/disbursement/update'), {
            SIPPointDisbursementID: sipPointDisbursementID,
            NewPoints: newPoints
        }),
    copyPrevious: () =>
        adminClient.post(buildUrl('/admin/disbursement/copy-previous')),
    commit: (periodId) =>
        adminClient.post(buildUrl(periodId ? `/admin/disbursement/commit?periodId=${periodId}` : '/admin/disbursement/commit')),
    deletePending: () =>
        adminClient.delete(buildUrl('/admin/disbursement/pending')),

    // ----- Welcome letters / password rotation -----
    getSupervisors: () =>
        adminClient.get(buildUrl(`/admin/supervisors?requestingEmployeeId=${adminEmployeeId()}`)),
    printWelcomeLetter: (employeeID) =>
        adminClient.get(buildUrl(`/admin/welcome-letter/${employeeID}`), { responseType: 'blob' }),
    printWelcomeLetters: (employeeIDs) => {
        const params = new URLSearchParams();
        employeeIDs.forEach((id) => params.append('employeeIds', String(id)));
        return adminClient.get(buildUrl(`/admin/welcome-letters?${params.toString()}`), { responseType: 'blob' });
    },

    // ----- Inventory -----
    getInventory: () =>
        adminClient.get(buildUrl('/admin/inventory')),
    updateInventoryCount: (sipSwagItemInventoryID, remainingQuantity) =>
        adminClient.put(buildUrl(`/admin/inventory/${sipSwagItemInventoryID}`), { RemainingQuantity: remainingQuantity }),
    // NOTE: path segment is "location", not "bin" — IIS request filtering 404s any
    // URL containing a /bin/ segment (it's a reserved/hidden segment on Windows/IIS).
    setInventoryBin: (sipSwagItemInventoryID, bin) =>
        adminClient.put(buildUrl(`/admin/inventory/${sipSwagItemInventoryID}/location`), { Bin: bin }),

    // ----- Monthly winners (wall-poster announcements) -----
    getWinners: (year, month) =>
        adminClient.get(buildUrl(`/admin/winners?year=${year}&month=${month}`)),
    saveSiteWinners: ({ year, month, siteID, winners }) =>
        adminClient.put(buildUrl('/admin/winners/site'), { Year: year, Month: month, SiteID: siteID, Winners: winners }),
    getWinnersHistory: (siteID, limitMonths = 12) =>
        adminClient.get(buildUrl(`/admin/winners/${siteID}/history?limitMonths=${limitMonths}`)),
    printWinnersAnnouncement: (siteID, year, month) =>
        adminClient.get(buildUrl(`/admin/winners/announcement/${siteID}/${year}/${month}`), { responseType: 'blob' }),

    // ----- Qualification Maintenance (Supervisor + Employee) -----
    getSupervisorQualifications: (siteID, year, month) =>
        adminClient.get(buildUrl(`/admin/supervisor-qual?siteId=${siteID}&year=${year}&month=${month}&requestingEmployeeId=${adminEmployeeId()}`)),
    saveSupervisorQualifications: ({ siteID, year, month, rows }) =>
        adminClient.put(buildUrl('/admin/supervisor-qual'), { SiteID: siteID, Year: year, Month: month, Rows: rows }),
    commitSupervisorQualifications: ({ siteID, year, month }) =>
        adminClient.post(buildUrl(`/admin/supervisor-qual/commit?siteId=${siteID}&year=${year}&month=${month}&committedBy=${adminEmployeeId()}`)),
    getPeriodCommitStatus: (year, month) =>
        adminClient.get(buildUrl(`/admin/period-commit-status?year=${year}&month=${month}`)),
    getEmployeeQualifications: (siteID, year, month) =>
        adminClient.get(buildUrl(`/admin/employee-qual?siteId=${siteID}&year=${year}&month=${month}&requestingEmployeeId=${adminEmployeeId()}`)),
    saveEmployeeQualifications: ({ siteID, year, month, rows }) =>
        adminClient.put(buildUrl('/admin/employee-qual'), { SiteID: siteID, Year: year, Month: month, Rows: rows }),
    setEmployeeInactive: (employeeID) =>
        adminClient.post(buildUrl(`/admin/employee/${employeeID}/inactive`)),
    getDrawingList: (siteID, year, month) =>
        adminClient.get(buildUrl(`/admin/drawing?siteId=${siteID}&year=${year}&month=${month}`)),

    // ----- Load Employees (Epicor roster import) -----
    loadEmployees: (rows) =>
        adminClient.post(buildUrl('/admin/load-employees'), { Rows: rows }),
    loadSupervisors: (rows) =>
        adminClient.post(buildUrl('/admin/load-supervisors'), { Rows: rows }),
    getEmployeeRoster: () =>
        adminClient.get(buildUrl('/admin/employee-roster')),
    getSupervisorRoster: () =>
        adminClient.get(buildUrl('/admin/supervisor-roster')),

    // ----- Historic Reports -----
    reportPoints: (monthsBack = 24) =>
        adminClient.get(buildUrl(`/admin/reports/points?monthsBack=${monthsBack}`)),
    reportRecordables: (monthsBack = 24) =>
        adminClient.get(buildUrl(`/admin/reports/recordables?monthsBack=${monthsBack}`)),
    reportCriteria: (monthsBack = 24) =>
        adminClient.get(buildUrl(`/admin/reports/criteria?monthsBack=${monthsBack}`)),
    reportWinners: (monthsBack = 24) =>
        adminClient.get(buildUrl(`/admin/reports/winners?monthsBack=${monthsBack}`)),

    // ----- Order Processing -----
    getOrders: (siteId = 0) =>
        adminClient.get(buildUrl(`/admin/orders?siteId=${siteId}`)),
    getOrderMonths: () =>
        adminClient.get(buildUrl('/admin/orders/months')),
    // omit year/month for the current (open) cart
    getOrderLines: ({ year, month, siteId = 0 }) =>
        adminClient.get(buildUrl(`/admin/orders/lines?siteId=${siteId}` + (year ? `&year=${year}&month=${month}` : ''))),
    getPickList: ({ year, month, siteId = 0 }) =>
        adminClient.get(buildUrl(`/admin/orders/picklist?siteId=${siteId}` + (year ? `&year=${year}&month=${month}` : ''))),
    pickConfirm: ({ inventoryId, boxId, quantity }) =>
        adminClient.post(buildUrl('/admin/orders/pick-confirm'), {
            SIPSwagItemInventoryID: inventoryId, SIPBoxID: boxId, Quantity: quantity
        }),

    // ----- Rights Management (SIPAdminAccessID bitmask) -----
    getAdminRights: () =>
        adminClient.get(buildUrl('/admin/rights')),
    setAdminRights: (employeeID, accessMask) =>
        adminClient.put(buildUrl('/admin/rights'), { EmployeeID: employeeID, SIPAdminAccessID: accessMask }),
    addAdmin: (employeeID) =>
        adminClient.post(buildUrl(`/admin/rights/${employeeID}/add`)),
    removeAdmin: (employeeID) =>
        adminClient.put(buildUrl('/admin/rights'), { EmployeeID: employeeID, SIPAdminAccessID: 0 }),
    resetAdminCode: (employeeID) =>
        adminClient.post(buildUrl(`/admin/rights/${employeeID}/reset-code`)),

    // ----- Annual Program Year -----
    getProgramYears: () =>
        adminClient.get(buildUrl('/admin/program-years')),
    saveProgramYear: (dto) =>
        adminClient.post(buildUrl('/admin/program-years'), dto),

    // ----- Swag Items (shared add/edit) -----
    getSwagLookups: () =>
        adminClient.get(buildUrl('/admin/swag/lookups')),
    getSwagItems: () =>
        adminClient.get(buildUrl('/admin/swag/items')),
    saveSwagItem: (dto) =>
        adminClient.post(buildUrl('/admin/swag/items'), dto),
    saveSelectionGroup: (dto) =>
        adminClient.post(buildUrl('/admin/swag/selection-group'), dto),
    getInventoryCandidates: () =>
        adminClient.get(buildUrl('/admin/swag/inventory-candidates')),
    addItemToInventory: (swagItemID) =>
        adminClient.post(buildUrl(`/admin/swag/items/${swagItemID}/add-to-inventory`)),
    savePointGroup: (dto) =>
        adminClient.post(buildUrl('/admin/swag/point-group'), dto),
    // multipart upload — use fetch so the browser sets the boundary
    uploadSwagImage: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return fetch(`${apiConfig.baseURL}${buildUrl('/admin/swag/image')}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('admin_auth_token')}`,
                'X-App-Source': 'SIPAdmin',
            },
            body: fd,
        });
    },
};

// Resolve a swag ImageName / ImagePath to a usable <img src>.
// Backward compatible: legacy names (and the "/images/<name>" the procs
// prepend) keep loading from the client's public folder; only "blob:<name>"
// values route to the backend streaming endpoint.
export function resolveSwagImage(raw) {
    // No image on file → show the "Coming Soon" placeholder (covers null and '').
    if (!raw || !String(raw).trim()) return SWAG_IMAGE_PLACEHOLDER;
    const v = String(raw);
    const m = v.match(/^\/images\/(.*)$/i);
    const name = m ? m[1] : v;
    if (name.startsWith('blob:')) {
        return `${apiConfig.baseURL}${apiConfig.apiPrefix}/swag-image/${encodeURIComponent(name.slice(5))}`;
    }
    return `${process.env.PUBLIC_URL || ''}/images/${name}`;
}

// Export the base URL for components that need it
export const getBackendUrl = () => apiConfig.baseURL;

// Export config for debugging
export { apiConfig };

export default apiClient;
